import {
  LoadingSpinner,
  ModalActions,
  ModalContent,
  ModalTitle,
  SearchInput,
} from "@msys/ui";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Alert, Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useCategories } from "../../categories/useCategories.js";
import { removeSubcategoriesOfDeletedParent } from "../../categories/helpers.js";
import { CatalogueGrid } from "../offeredServices/CatalogueGrid.js";
import {
  buildTaxonomyIndex,
  type CatalogueBuffer,
  expandedNonLeafIds,
  matrixCellKey,
  parseMatrixCellKey,
  prefillBufferFromVerticals,
  subtreeIds,
} from "../offeredServices/catalogueModel.js";
import {
  AUTO_FILLED_PROJECT_TYPES,
  OFFERED_PROJECT_TYPES,
} from "../offeredServices/projectTypes.js";
import type {
  OnboardingFlowStage,
  OnboardingViewer,
} from "./OnboardingFlowModal.js";
import { useSetOrganisationOfferedServices } from "./useSetOrganisationOfferedServices.js";
import type {
  OnboardingFlowWizardAction,
  OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

interface Props {
  // The modal's already-loaded viewer; stages must not re-subscribe to the
  // modal query (each subscription can refetch the suggestions field).
  viewer: OnboardingViewer;
  state: OnboardingFlowWizardState;
  dispatch: React.Dispatch<OnboardingFlowWizardAction>;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

export const OnboardingFlowSpecifyMatrixStage = ({
  viewer,
  state,
  dispatch,
  setStage,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const { categories: allCategories, loading: categoriesLoading } =
    useCategories();
  const [search, setSearch] = React.useState("");

  // Belt-and-suspenders against direct-URL access; server also gates this.
  React.useEffect(() => {
    if (viewer.organisation.organisationType !== "CRAFTSMAN") {
      setStage("success");
    }
  }, [viewer, setStage]);

  const usableCategories = React.useMemo(
    () => removeSubcategoriesOfDeletedParent(allCategories, allCategories),
    [allCategories]
  );

  const index = React.useMemo(
    () => buildTaxonomyIndex(usableCategories),
    [usableCategories]
  );

  const { commit, error, loading } = useSetOrganisationOfferedServices(
    state,
    setStage,
    index
  );

  const buffer = React.useMemo<CatalogueBuffer>(() => {
    const declared = new Set(state.matrixDeclaredCategories);
    const selections = new Set<string>();
    for (const s of state.matrixSelections) {
      selections.add(matrixCellKey(s.categoryId, s.projectType));
    }
    return { declared, selections };
  }, [state.matrixDeclaredCategories, state.matrixSelections]);

  const handleBufferChange = React.useCallback(
    (next: CatalogueBuffer) => {
      dispatch({
        type: "setMatrixBuffer",
        declaredCategories: [...next.declared],
        selections: [...next.selections].map(parseMatrixCellKey),
      });
    },
    [dispatch]
  );

  // Expand every non-leaf category in the selected verticals' subtrees (which
  // the prefill declares in full) so their descendants are visible on entry —
  // not just the root verticals. Derived from `selectedVerticals`, so it is
  // stable and doesn't fight the user's manual expand/collapse.
  const initiallyExpandedIds = React.useMemo(() => {
    const declared = new Set<string>();
    for (const verticalId of state.selectedVerticals) {
      for (const id of subtreeIds(index, verticalId)) declared.add(id);
    }
    return expandedNonLeafIds(declared, index);
  }, [state.selectedVerticals, index]);

  // Ref-guarded so revisits to this stage preserve the user's edits. Seeds the
  // buffer from the wizard's earlier choices: every selected vertical's subtree
  // is declared (with its ancestors), and each leaf is pre-checked for the
  // project types the user picked — so entering the matrix reflects the
  // project-types step rather than auto-filling all five.
  const hasPrefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (hasPrefilledRef.current) return;
    if (
      state.matrixDeclaredCategories.length > 0 ||
      state.matrixSelections.length > 0
    ) {
      hasPrefilledRef.current = true;
      return;
    }
    if (state.selectedVerticals.length === 0 || usableCategories.length === 0) {
      return;
    }
    hasPrefilledRef.current = true;
    const { declared, selections } = prefillBufferFromVerticals(
      index,
      state.selectedVerticals,
      state.selectedProjectTypes
    );
    dispatch({
      type: "setMatrixBuffer",
      declaredCategories: [...declared],
      selections: [...selections].map(parseMatrixCellKey),
    });
  }, [
    state.selectedVerticals,
    state.selectedProjectTypes,
    state.matrixDeclaredCategories.length,
    state.matrixSelections.length,
    usableCategories.length,
    index,
    dispatch,
  ]);

  if (categoriesLoading) {
    return <LoadingSpinner />;
  }
  if (viewer.organisation.organisationType !== "CRAFTSMAN") {
    return null;
  }

  return (
    <>
      <ModalTitle
        title={t("Specify your offered services", {
          ns: "OrganisationProfile",
        })}
      />
      <ModalContent>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error">
              {error.message ||
                t("Something went wrong. Please try again.", {
                  ns: "Global",
                })}
            </Alert>
          )}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              py: 1,
              backgroundColor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <SearchInput
              placeholder={t("Search categories", {
                ns: "OrganisationProfile",
              })}
              value={search}
              onChangeSearchTerm={setSearch}
              size="extra-small"
              sx={{
                transition: "ease-in-out 250ms max-width",
                maxWidth: "100%",
                ":not(:focus-within)": { maxWidth: 320 },
              }}
            />
          </Box>
          <CatalogueGrid
            categories={usableCategories}
            index={index}
            buffer={buffer}
            search={search}
            projectTypes={OFFERED_PROJECT_TYPES}
            autoFillProjectTypes={AUTO_FILLED_PROJECT_TYPES}
            onBufferChange={handleBufferChange}
            showOverrides={false}
            initiallyExpandedIds={initiallyExpandedIds}
          />
        </Stack>
      </ModalContent>
      <ModalActions
        actionButtons={[
          {
            label: t("Back", { ns: "Global" }),
            handleClick: () => setStage("projectTypes"),
            buttonProps: {
              color: "primary",
              variant: "text",
              startIcon: <ChevronLeftIcon />,
              disabled: loading,
            },
          },
          {
            label: t("Skip", { ns: "Global" }),
            handleClick: () => void commit("skip-matrix"),
            buttonProps: {
              color: "primary",
              variant: "text",
              endIcon: <KeyboardDoubleArrowRightIcon />,
              disabled: loading,
            },
          },
          {
            label: t("Continue", { ns: "Global" }),
            handleClick: () => void commit("specify-now-commit"),
            buttonProps: {
              color: "primary",
              variant: "contained",
              endIcon: <ChevronRightIcon />,
              // Only declared categories are committed — the commit selector
              // filters selections to declared rows, so session-memory cells for
              // undeclared categories would yield an empty replace-all payload.
              // Bare declarations (categories without services) are valid.
              disabled: loading || state.matrixDeclaredCategories.length === 0,
            },
          },
        ]}
      />
    </>
  );
};
